import { useCallback, useEffect, useRef, useState } from 'react'

import * as Sentry from '@sentry/nextjs'

import {
  useCreateGridAnalysisJobMutation,
  useGridAnalysisJobQuery,
} from '@/graphql/generated/hooks'
import {
  BoxInput,
  GridAnalysisJob,
  GridAnalysisJobStatus,
} from '@/graphql/generated/schemas'

const POLL_INTERVAL_MS = 3000
const POLL_TIMEOUT_MS = 90000

export const TIMEOUT_MESSAGE =
  'The operation timed out. Please try again later.'

export type GridAnalysisJobUiStatus = GridAnalysisJobStatus | null

interface StartArgsI {
  incidentId: string
  labeledIn: number
  labeledOut: number
  inBox: BoxInput
  outBox: BoxInput
}

interface UseGridAnalysisJobOptionsI {
  onCompleted?: () => void
  onFailed?: (isTimeout: boolean) => void
}

export const useGridAnalysisJob = (options?: UseGridAnalysisJobOptionsI) => {
  const onCompletedRef = useRef(options?.onCompleted)
  const onFailedRef = useRef(options?.onFailed)
  onCompletedRef.current = options?.onCompleted
  onFailedRef.current = options?.onFailed
  const hasNotifiedRef = useRef(false)

  const [jobId, setJobId] = useState<string | null>(null)
  const [status, setStatus] = useState<GridAnalysisJobUiStatus>(null)
  const [error, setError] = useState<Error | undefined>()
  const [isTimeout, setIsTimeout] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [createMutation] = useCreateGridAnalysisJobMutation()

  const {
    data,
    error: queryError,
    stopPolling,
  } = useGridAnalysisJobQuery({
    variables: { id: jobId ?? '' },
    skip: !jobId,
    fetchPolicy: 'network-only',
    pollInterval: POLL_INTERVAL_MS,
  })

  const job = data?.gridAnalysisJob as GridAnalysisJob | undefined

  const clearPollTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  // Handle job status transitions
  useEffect(() => {
    if (!job) return
    if (hasNotifiedRef.current) return
    if (job.status === GridAnalysisJobStatus.Completed) {
      hasNotifiedRef.current = true
      clearPollTimeout()
      setStatus(GridAnalysisJobStatus.Completed)
      stopPolling()
      onCompletedRef.current?.()
    } else if (job.status === GridAnalysisJobStatus.Failed) {
      hasNotifiedRef.current = true
      const failure = new Error(
        `Grid analysis job failed (id: ${job.id ?? 'unknown'})`
      )
      Sentry.captureException(failure, {
        extra: { jobId: job.id, jobStatus: job.status },
      })
      clearPollTimeout()
      setStatus(GridAnalysisJobStatus.Failed)
      setError(failure)
      stopPolling()
      onFailedRef.current?.(false)
    }
  }, [job, stopPolling, clearPollTimeout])

  // Handle network/GraphQL errors during polling
  useEffect(() => {
    if (
      queryError &&
      status === GridAnalysisJobStatus.Pending &&
      !hasNotifiedRef.current
    ) {
      hasNotifiedRef.current = true
      Sentry.captureException(queryError, { extra: { jobId } })
      clearPollTimeout()
      setStatus(GridAnalysisJobStatus.Failed)
      setError(queryError)
      stopPolling()
      onFailedRef.current?.(false)
    }
  }, [queryError, status, jobId, stopPolling, clearPollTimeout])

  // Clean up timer on unmount
  useEffect(() => clearPollTimeout, [clearPollTimeout])

  const start = useCallback(
    async ({
      incidentId,
      labeledIn,
      labeledOut,
      inBox,
      outBox,
    }: StartArgsI) => {
      stopPolling()
      setJobId(null)
      setError(undefined)
      setIsTimeout(false)
      hasNotifiedRef.current = false
      setStatus(GridAnalysisJobStatus.Pending)
      try {
        const result = await createMutation({
          variables: {
            input: { incidentId, labeledIn, labeledOut, inBox, outBox },
          },
        })
        const newId = result.data?.createGridAnalysisJob?.gridAnalysisJob?.id
        if (!newId) {
          throw new Error('No job id returned from createGridAnalysisJob')
        }
        setJobId(newId)

        // Kick off the FE-side watchdog. If the job hasn't transitioned out of
        // PENDING within POLL_TIMEOUT_MS, we stop polling, flip to FAILED, and
        // surface a timeout message. Backend sweeps stale jobs every 10 min
        // anyway, but the UX ceiling is tighter.
        clearPollTimeout()
        timeoutRef.current = setTimeout(() => {
          if (hasNotifiedRef.current) return
          hasNotifiedRef.current = true
          stopPolling()
          setIsTimeout(true)
          setStatus(GridAnalysisJobStatus.Failed)
          const timeoutErr = new Error(
            `Grid analysis job polling timed out after ${POLL_TIMEOUT_MS}ms (id: ${newId})`
          )
          Sentry.captureException(timeoutErr, {
            extra: { jobId: newId, timeoutMs: POLL_TIMEOUT_MS },
          })
          setError(timeoutErr)
          onFailedRef.current?.(true)
        }, POLL_TIMEOUT_MS)
      } catch (e) {
        Sentry.captureException(e)
        hasNotifiedRef.current = true
        setStatus(GridAnalysisJobStatus.Failed)
        setError(e instanceof Error ? e : new Error(String(e)))
        onFailedRef.current?.(false)
      }
    },
    [createMutation, stopPolling, clearPollTimeout]
  )

  const reset = useCallback(() => {
    clearPollTimeout()
    stopPolling()
    setJobId(null)
    setStatus(null)
    setError(undefined)
    setIsTimeout(false)
    hasNotifiedRef.current = false
  }, [stopPolling, clearPollTimeout])

  return { start, reset, job, status, error, isTimeout }
}
